import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    AfterViewInit,
} from '@angular/core';
import { AppStore, VisitsRepartitionDatas, RangeBegin } from '@app/models';
import { StoreProvider } from '@app/providers';
import { CountingService } from '@app/services';
import { CountingCircleStateService } from '@app/services/counting-circle-state.service';
import { Subscription } from 'rxjs';
import { CALENDAR_SELECT, CALENDAR_TYPE } from '@app/models/Calendar';
import Chart from 'chart.js/auto';

@Component({
    standalone: false,
    selector: 'app-counting-visit-repartition',
    templateUrl: './counting-visitRepartition.component.html',
    styleUrls: ['counting-visitRepartition.component.scss'],
})
export class VisitRepartitionComponent
    implements OnDestroy, OnInit, AfterViewInit, OnChanges
{
    @Input() businessId!: string | number | undefined;
    @Input() format!: CALENDAR_SELECT;
    @Input() range!: RangeBegin;

    public data!: VisitsRepartitionDatas;
    public dataVs!: VisitsRepartitionDatas;

    public _value: number[] = [];
    public _valueVs: number[] = [];
    public _labels: string[] = [];
    public _labelsVs: string[] = [];

    @Input() calendarSelect!: CALENDAR_SELECT;
    @Input() calendarType!: CALENDAR_TYPE;

    public emptyState = false;
    public isClosed = false;
    public loading = false;

    public app!: AppStore;
    private $storeSubs!: Subscription;
    private dataSub?: Subscription;
    private charts: Chart[] = [];
    private viewInitialized = false;

    public averageDisplay = {
        versus: 0,
        curent: 0,
    };

    constructor(
        private $store: StoreProvider,
        private countingService: CountingService,
        private circleState: CountingCircleStateService
    ) {}

    ngOnInit() {
        this.$storeSubs = this.$store
            .listen('app')
            .subscribe((app) => (this.app = app));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            (changes['businessId'] || changes['format'] || changes['range']) &&
            this.businessId != null &&
            this.format &&
            this.range
        ) {
            this.circleState.setLoading();
            this.loadData();
        }
    }

    private lastComparisonNbPerson = 0;

    private loadData(): void {
        this.loading = true;
        this.dataSub?.unsubscribe();

        this.dataSub = this.countingService
            .getVisitsRepartitionData(
                Number(this.businessId),
                this.format,
                this.range
            )
            .subscribe({
                next: (result: any) => {
                    this.data = result.current;
                    this.dataVs = result.versus;
                    this.lastComparisonNbPerson =
                        result.comparison?.nbPerson ?? 0;
                    this.isClosed = result.closed === true;

                    this.parseData();
                    this.loading = false;

                    this.emitCircleData();

                    setTimeout(() => this.renderCharts(), 0);
                },
                error: () => {
                    this.loading = false;
                    this.isClosed = false;
                    this.emptyState = true;
                },
            });
    }

    private emitCircleData(): void {
        if (this.isClosed) {
            this.circleState.emit({
                current: 0,
                versus: 0,
                variation: 0,
                icon: '/assets/svg/customers-dark.svg',
            });
            return;
        }

        if (!this.data || !this.dataVs) return;

        this.circleState.emit({
            current: this.data.nbPerson,
            versus: this.dataVs.nbPerson,
            variation: this.lastComparisonNbPerson,
            icon: '/assets/svg/customers-dark.svg',
        });
    }

    private computeVariation(current: number, versus: number): number {
        if (!versus) return 0;
        return ((current - versus) / versus) * 100;
    }

    private parseData(): void {
        this.emptyState = false;
        this._value = [];
        this._valueVs = [];
        this._labels = [];
        this._labelsVs = [];

        if (
            this.isClosed ||
            (!this.data?.nbVisitsByLevel && !this.dataVs?.nbVisitsByLevel)
        ) {
            this.emptyState = true;
            return;
        }

        this._value = Object.values(this.data.nbVisitsByLevel);
        this._valueVs = Object.values(this.dataVs.nbVisitsByLevel);
        this._labels = Object.keys(this.data.nbVisitsByLevel);
        this._labelsVs = Object.keys(this.dataVs.nbVisitsByLevel);

        this.averageDisplay.curent = this.data.averageVisits;
        this.averageDisplay.versus = this.dataVs.averageVisits;
    }

    ngAfterViewInit() {
        this.viewInitialized = true;
        // Si les données sont déjà arrivées (appel rapide), on rend ici aussi
        if (this.data && this.dataVs && !this.loading) {
            this.renderCharts();
        }
    }

    private renderCharts(): void {
        if (!this.viewInitialized) return;

        this.destroyCharts();
        if (this.emptyState || !this._labels.length) return;

        const labelsList = this.getMaxArray();
        labelsList.forEach((labels, index) => {
            const canvas = document.getElementById(
                'chartRepartition' + index
            ) as HTMLCanvasElement;
            if (!canvas) return;

            this.charts.push(
                new Chart(canvas, {
                    data: {
                        datasets: [
                            {
                                data: [this._value[index] ?? 0],
                                borderColor: '#e3a719',
                                backgroundColor: '#e3a719',
                                barThickness: 12,
                                borderRadius: {
                                    bottomRight: 25,
                                    topRight: 25,
                                },
                            },
                            {
                                data: [this._valueVs[index] ?? 0],
                                borderColor: '#024F5D',
                                backgroundColor: '#024F5D',
                                barThickness: 12,
                                borderRadius: {
                                    bottomRight: 25,
                                    topRight: 25,
                                },
                            },
                        ],
                        labels: [labels],
                    },
                    type: 'bar',
                    options: {
                        indexAxis: 'y',
                        elements: {
                            bar: {
                                borderWidth: 2,
                            },
                        },
                        responsive: true,
                        scales: {
                            y: {
                                ticks: {
                                    display: false,
                                    autoSkip: false,
                                    stepSize: 20,
                                },
                            },
                            x: {
                                beginAtZero: true,
                                display: false,
                                max: Math.max(
                                    ...this._value,
                                    ...this._valueVs,
                                    1
                                ),
                            },
                        },
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            title: { display: false },
                            datalabels: { display: false },
                            tooltip: { enabled: false },
                        },
                    },
                })
            );
        });
    }

    private destroyCharts(): void {
        this.charts.forEach((chart) => chart.destroy());
        this.charts = [];
    }

    getMaxArray() {
        return this._labels.length >= this._labelsVs.length
            ? this._labels
            : this._labelsVs;
    }

    ngOnDestroy(): void {
        this.$storeSubs?.unsubscribe();
        this.dataSub?.unsubscribe();
        this.destroyCharts();
    }
}
